import React from "react";

import Input from "@material-ui/core/Input";
import PropTypes from "prop-types";

import useForceUpdate from "../../hooks/useForceUpdate";
import useRbTextInputStyles from "./useRbTextInputStyles";

const RbTextInput = React.forwardRef(function RbTextInput(props, ref) {
  const {
    value,
    format,
    inputRef: inputRefProp,
    parse,
    rounded,
    onChange,
    onChangeValue,
    onKeydown,
    ...other
  } = props;

  const classes = useRbTextInputStyles(props);
  const forceUpdate = useForceUpdate();
  const inputRef = React.useRef();
  const selectionEndRef = React.useRef();
  const lengthRef = React.useRef();
  const keyRef = React.useRef();

  const handleChange = React.useCallback(
    (event) => {
      if (onChange) {
        onChange(event);
      }
      if (onChangeValue) {
        const parsed = parse(event.target.value);
        if (parsed !== value) {
          onChangeValue(parsed === "" ? undefined : parsed);
        } else {
          forceUpdate();
        }
      }
    },
    [onChange, onChangeValue, parse, value, forceUpdate]
  );

  const handleKeydown = React.useCallback(
    (event) => {
      const { target, key } = event;
      selectionEndRef.current = target.selectionEnd;
      lengthRef.current = target.value.length;
      keyRef.current = key;
      if (onKeydown) {
        onKeydown(event);
      }
    },
    [onKeydown]
  );

  const handleRef = React.useCallback(
    (elem) => {
      inputRef.current = elem;
      if (inputRefProp) {
        inputRefProp.current = elem;
      }
    },
    [inputRefProp]
  );

  const formattedValue = format(value);

  React.useLayoutEffect(() => {
    if (!formattedValue || inputRef.current !== document.activeElement) {
      return;
    }

    const { length } = formattedValue;
    let diff = length - lengthRef.current;

    if (keyRef.current === "Backspace") {
      if (diff === 0) {
        diff--;
      }
    } else if (keyRef.current === "Delete") {
      if (diff === 0) {
        diff++;
      } else {
        diff = 0;
      }
    }

    let cursorIndex = selectionEndRef.current + diff;
    if (cursorIndex < 0) {
      cursorIndex = 0;
    }

    inputRef.current.setSelectionRange(cursorIndex, cursorIndex);
  });

  return (
    <Input
      {...other}
      ref={ref}
      classes={classes}
      inputRef={handleRef}
      onChange={handleChange}
      onKeyDown={handleKeydown}
      value={formattedValue}
    />
  );
});

RbTextInput.propTypes = {
  autoComplete: PropTypes.string,
  disableUnderline: PropTypes.bool,
  format: PropTypes.func,
  fullWidth: PropTypes.bool,
  inputRef: PropTypes.object,
  onChange: PropTypes.func,
  onChangeValue: PropTypes.func,
  onKeydown: PropTypes.func,
  parse: PropTypes.func,
  rounded: PropTypes.bool,
  value: PropTypes.any,
};

RbTextInput.defaultProps = {
  autoComplete: "off",
  disableUnderline: true,
  format: (v) => v,
  fullWidth: true,
  inputRef: undefined,
  onChange: undefined,
  onChangeValue: undefined,
  onKeydown: undefined,
  parse: (v) => v,
  rounded: false,
  value: undefined,
};

export default React.memo(RbTextInput);
